import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import { Col, Row, Typography } from "antd";
import { SvgIcon } from "assets/icons";
import FormInput from "components/FormInput";
import { INPUT_TYPE } from "config/constants";
import Card from "features/Cart/components/Card";
import { ORDER_PRIORITY } from "features/Cart/constants";
import { renderOrderPriority } from "features/Order/helper";
import { t } from "i18next";
import { compact, isEmpty, join } from "lodash";
import moment from "moment";
import { formatDateTime } from "utils/dateTime";
import AdditionalDeliver from "../AdditionalDeliver";
import InfoReceiverAndSender from "../InfoReceiverAndSender";
import SelectSeller from "../SelectSeller";
import ShippingPolicyNotes from "../ShippingPolicyNotes";
import "./index.scss";
import useInfoAgent from "./useInfoAgent";
import { useQuery } from "@apollo/client";
import { GET_WAREHOUSES_BY_SELLER } from "graphql/ware-house/query";

const InfoAgent = ({
  data,
  handleUpdate,
  handleUpdateSeller,
  loadingUpdateCart,
  refetchCart,
  useInOrder,
  idCart,
  form,
  dataOrder,
  dataShippingProvider,
  loadingShippingProvider,
  shippingTypes,
}) => {
  const {
    orderTypeOptions,
    infoReceiverAndSender,
    shippingTypeOptions,
    orderPriorityOptions,
    onChange,
    onChangeDate,
    effectiveShippingType,
    isShippingTypeInvalid,
  } = useInfoAgent({ data, handleUpdate, refetchCart, shippingTypes, dataOrder });

  const sellerID = data?.seller?.id;
  const { data: dataWarehouses } = useQuery(GET_WAREHOUSES_BY_SELLER, {
    variables: { sellerID },
    skip: !sellerID,
    fetchPolicy: "network-only",
  });
  const warehouses = dataWarehouses?.warehouse?.list?.warehouses || [];

  const warehouseOptions = warehouses?.map((item) => ({
    value: item?.id,
    label: (
      <Row gutter={16}>
        <Col span={7}>
          <Typography.Text style={{ fontSize: "13px" }} ellipsis>
            {item?.name}
          </Typography.Text>
        </Col>
        <Col span={14}>
          <Typography.Text ellipsis style={{ fontSize: "13px" }}>
            {compact([item?.address, item?.ward?.name, item?.district?.name, item?.city?.name]).join(" ,")}
          </Typography.Text>
        </Col>
        <Col span={3}>
          <Typography.Text ellipsis style={{ fontSize: "13px" }}>
            {item?.branch?.name}
          </Typography.Text>
        </Col>
      </Row>
    ),
    warehouses: item?.warehouses,
    labelWarehouse: `${item?.branch?.name} - ${item?.name}`,
  }));

  const shippingType = effectiveShippingType;

  const renderOrderPriorityLabel = {
    [`${ORDER_PRIORITY.LOW}`]: (
      <Typography.Text strong style={{ color: "#052987" }}>
        {renderOrderPriority[data?.orderPriority]}
      </Typography.Text>
    ),
    [`${ORDER_PRIORITY.MEDIUM}`]: (
      <Typography.Text strong style={{ color: "#00AB78" }}>
        {renderOrderPriority[data?.orderPriority]}
      </Typography.Text>
    ),
    [`${ORDER_PRIORITY.HIGH}`]: (
      <Typography.Text strong style={{ color: "#D2434D" }}>
        {renderOrderPriority[data?.orderPriority]}
      </Typography.Text>
    ),
  };

  return (
    <Card
      title={
        <Typography.Text strong style={{ color: "#2246DC" }}>
          {t("cart.infoAgent")}
        </Typography.Text>
      }
    >
      <Row gutter={16} className="cart-form-controller">
        <Col span={14} className="left">
          <Row gutter={12}>
            <Col span={16}>
              {isEmpty(useInOrder) ? (
                <SelectSeller handleUpdateSeller={handleUpdateSeller} refetchCart={refetchCart} isPadding={true} />
              ) : (
                <Typography.Text strong style={{ color: "#052987" }}>
                  {join(compact([data?.seller?.fullName, data?.seller?.telephone]), " | ")}
                </Typography.Text>
              )}
            </Col>

            <Col span={8}>
              <FormInput
                type={INPUT_TYPE.SELECT}
                inputOptions={{
                  options: orderTypeOptions,
                  style: { width: "100%" },
                  onChange: (value) => onChange({ orderType: value }),
                }}
                formItemOptions={{ name: "orderType", style: { marginBottom: 12 } }}
              />
            </Col>
            <Col span={24} style={{ marginBottom: 12 }}>
              <Row gutter={[16, 12]}>
                {infoReceiverAndSender.map((item, index) => (
                  <Col span={24} key={index}>
                    <InfoReceiverAndSender
                      infoUser={item}
                      loadingUpdateCart={loadingUpdateCart}
                      refetch={refetchCart}
                    />
                  </Col>
                ))}
              </Row>
            </Col>
            {isEmpty(useInOrder) || useInOrder?.changeShippingFee ? (
              <Col span={24}>
                <FormInput
                  type={INPUT_TYPE.RADIO_GROUP}
                  formItemOptions={{
                    label: <b>{t("cart.deliveryMethodLabel")}</b>,
                    style: { width: "100%", marginBottom: 12 },
                    name: "shippingType",
                    required: true,
                    className: "shipping-type-form-item required-mark-right",
                  }}
                  inputOptions={{
                    options: shippingTypeOptions,
                    onChange: (value) => onChange({ shippingType: value?.target?.value }),
                  }}
                />
              </Col>
            ) : (
              <>
                <Col span={6}>
                  <Typography.Text strong>{t("cart.deliveryMethodLabel")}</Typography.Text>
                </Col>
                <Col span={6}>
                  {/* Cùng nguồn với radio group ở nhánh sửa được phía trên (`getShippingTypesV2`),
                      nên chế độ xem và chế độ sửa không bao giờ lệch tên PTVC. BE không trả thì "--". */}
                  <Typography.Text strong style={{ color: "#052987" }}>
                    {shippingTypeOptions?.find((option) => option?.value === data?.shippingType)?.label || "--"}
                  </Typography.Text>
                </Col>
                <Col span={12}></Col>
              </>
            )}
            <AdditionalDeliver
              dataOrder={dataOrder}
              shippingType={shippingType}
              handleUpdate={handleUpdate}
              refetchCart={refetchCart}
              useInOrder={useInOrder}
              dataShippingProvider={dataShippingProvider}
              form={form}
              loadingShippingProvider={loadingShippingProvider}
              isShippingTypeInvalid={isShippingTypeInvalid}
            />
            <Col span={24}>
              <div style={{ paddingLeft: "175px", paddingTop: "8px" }}>
                <ShippingPolicyNotes />
              </div>
            </Col>
          </Row>
        </Col>

        <Col span={6} className="center">
          {isEmpty(useInOrder) ? (
            <>
              <FormInput
                type={INPUT_TYPE.SELECT}
                formItemOptions={{
                  label: <b>{t("cart.exportWarehouseLabel")}</b>,
                  style: { width: "100%" },
                  name: ["exportedWarehouse", "id"],
                  rules: [{ required: true, message: t("cart.requiredWarehouse") }],
                  className: "required-mark-right center-field-form-item",
                }}
                inputOptions={{
                  optionLabelProp: "labelWarehouse",
                  options: warehouseOptions,
                  dropdownMatchSelectWidth: 900,
                  dropdownRender: (originNode) => (
                    <>
                      <Row gutter={[16]} style={{ background: "#F7F8FB" }}>
                        <Col
                          span={7}
                          style={{
                            textAlign: "center",
                            padding: "12px 8px",
                            color: "#5B6673",
                            fontWeight: "bold",
                          }}
                        >
                          <Typography.Text>Tên kho</Typography.Text>
                        </Col>
                        <Col
                          span={14}
                          style={{
                            textAlign: "center",
                            padding: "12px 8px",
                            color: "#5B6673",
                            fontWeight: "bold",
                          }}
                        >
                          <Typography.Text>Địa chỉ kho</Typography.Text>
                        </Col>
                        <Col
                          span={3}
                          style={{
                            textAlign: "center",
                            padding: "12px 8px",
                            color: "#5B6673",
                            fontWeight: "bold",
                          }}
                        >
                          <Typography.Text>Chi nhánh</Typography.Text>
                        </Col>
                      </Row>
                      {originNode}
                    </>
                  ),
                  placeholder: t("cart.warehousePlaceholder"),
                  onChange: (value) => onChange({ exportedWarehouseID: value }),
                }}
              />
              <FormInput
                type={INPUT_TYPE.SELECT}
                formItemOptions={{
                  label: <b>{t("cart.orderPriorityLabel")}</b>,
                  style: { width: "100%" },
                  name: "orderPriority",
                  className: "center-field-form-item",
                }}
                inputOptions={{
                  options: orderPriorityOptions,
                  onChange: (value) => onChange({ orderPriority: value }),
                }}
              />

              <FormInput
                type={INPUT_TYPE.DATE_PICKER}
                formItemOptions={{
                  name: "deliveryDeadline",
                  label: <b>{t("cart.promisedDeliverTimeLabel")}</b>,
                  style: { width: "100%" },
                  className: "center-field-form-item",
                }}
                inputOptions={{
                  format: "HH:mm DD/MM/YYYY",
                  showTime: true,
                  showNow: false,
                  disabledDate: (current) => {
                    return current && current < moment().startOf("day");
                  },
                  style: { width: "100%" },
                  placeholder: t("cart.promisedDeliverTimePlaceholder"),
                  onChange: onChangeDate,
                }}
              />

              <FormInput
                type={INPUT_TYPE.SWITCH}
                formItemOptions={{
                  label: <b>{t("cart.fullStockRequiredLabel")}</b>,
                  style: { width: "100%" },
                  tooltip: {
                    title: t("cart.fullStockRequiredLabelTooltip"),
                    icon: <SvgIcon.InfoCircleIcon />,
                  },
                  name: "fullStockRequired",
                  valuePropName: "checked",
                }}
                inputOptions={{
                  onChange: (value) => onChange({ fullStockRequired: value }),
                }}
              />
            </>
          ) : (
            <Row gutter={[16, 8]}>
              <Col span={12}>
                <Typography.Text strong>{t("cart.exportWarehouseLabel")}</Typography.Text>
              </Col>
              <Col span={12}>
                <Typography.Text strong style={{ color: "#052987" }}>
                  {data?.exportedWarehouse?.name}
                </Typography.Text>
              </Col>
              <Col span={12}>
                <Typography.Text strong>{t("cart.orderPriorityLabel")}</Typography.Text>
              </Col>
              <Col span={12}>{renderOrderPriorityLabel[data?.orderPriority]}</Col>
              <Col span={12}>
                <Typography.Text strong>{t("cart.promisedDeliverTimeLabel")}</Typography.Text>
              </Col>
              <Col span={12}>
                <Typography.Text strong style={{ color: "#052987" }}>
                  {data?.deliveryDeadline && formatDateTime(data?.deliveryDeadline)}
                </Typography.Text>
              </Col>
              <Col span={24}>
                {data?.fullStockRequired ? (
                  <CheckCircleFilled style={{ color: "#00AB78" }} />
                ) : (
                  <CloseCircleFilled style={{ color: "#D2434D" }} />
                )}
                <Typography.Text strong style={{ marginLeft: "8px" }}>
                  {t("cart.fullStockRequiredLabel")}
                </Typography.Text>
              </Col>
            </Row>
          )}
        </Col>
        <Col span={4} className="right">
          <FormInput
            type={INPUT_TYPE.SWITCH}
            formItemOptions={{
              label: <b>{t("cart.vatAllOrderLabel")}</b>,
              style: { width: "100%" },
              name: "vat",
              valuePropName: "checked",
            }}
            inputOptions={{
              disabled: true,
              onBlur: () => handleUpdate(),
            }}
          />
          <FormInput
            inputOptions={{
              placeholder: t("cart.vatTaxIDNumberPlaceholder"),
              disabled: true,
              onBlur: () => handleUpdate(),
            }}
            formItemOptions={{
              name: "vatTaxIDNumber",
            }}
          />

          <FormInput
            inputOptions={{
              placeholder: t("cart.vatCompanyNamePlaceholder"),
              disabled: true,
              onBlur: () => handleUpdate(),
            }}
            formItemOptions={{
              name: "vatCompanyName",
            }}
          />

          <FormInput
            inputOptions={{
              placeholder: t("cart.vatEmailPlaceholder"),
              disabled: true,
              onBlur: () => handleUpdate(),
            }}
            formItemOptions={{
              name: "vatEmail",
              rules: [{ type: "email", message: t("common.wrongFormatEmail") }],
            }}
          />

          <FormInput
            inputOptions={{
              placeholder: t("cart.vatAddressPlaceholder"),
              disabled: true,
              onBlur: () => handleUpdate(),
            }}
            formItemOptions={{
              name: "vatAddress",
            }}
          />
        </Col>
      </Row>
    </Card>
  );
};

export default InfoAgent;
